import { DAY_OPTIONS } from "@/lib/constants/classes";

export interface ClassesPageUrlState {
  search: string;
  season: string | null;
  location: string | null;
  teacher: string | null;
  days: string[];
  /** 1-based page number for the URL; omitted from query when 1. */
  page: number;
}

export const CLASSES_PAGE_SIZE = 5;

export const EMPTY_CLASSES_PAGE_URL_STATE: ClassesPageUrlState = {
  search: "",
  season: null,
  location: null,
  teacher: null,
  days: [],
  page: 1,
};

const VALID_DAYS = new Set<string>(DAY_OPTIONS);
const DAY_BY_LOWER = new Map(
  DAY_OPTIONS.map((day) => [day.toLowerCase(), day] as const),
);

function parsePage(value: string | null): number {
  if (value == null || value.trim() === "") return 1;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
}

function parseOptionalString(value: string | null): string | null {
  const trimmed = value?.trim() ?? "";
  return trimmed ? trimmed : null;
}

/** Teacher filter is stored as a numeric teacher id string. */
function parseTeacherId(value: string | null): string | null {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return null;
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed <= 0 || String(parsed) !== trimmed) {
    return null;
  }
  return trimmed;
}

function parseDays(value: string | null): string[] {
  if (!value?.trim()) return [];

  const seen = new Set<string>();
  const days: string[] = [];

  for (const part of value.split(",")) {
    const canonical = DAY_BY_LOWER.get(part.trim().toLowerCase());
    if (!canonical || seen.has(canonical)) continue;
    if (!VALID_DAYS.has(canonical)) continue;
    seen.add(canonical);
    days.push(canonical);
  }

  return days;
}

function daysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((day, index) => day === b[index]);
}

export function classesPageUrlStateEqual(
  a: ClassesPageUrlState,
  b: ClassesPageUrlState,
): boolean {
  return (
    a.search === b.search &&
    a.season === b.season &&
    a.location === b.location &&
    a.teacher === b.teacher &&
    a.page === b.page &&
    daysEqual(a.days, b.days)
  );
}

export function parseClassesPageParamsFromSearchParams(
  searchParams: URLSearchParams,
): ClassesPageUrlState {
  return {
    search: searchParams.get("q")?.trim() ?? "",
    season: parseOptionalString(searchParams.get("season")),
    location: parseOptionalString(searchParams.get("location")),
    teacher: parseTeacherId(searchParams.get("teacher")),
    days: parseDays(searchParams.get("days")),
    page: parsePage(searchParams.get("page")),
  };
}

export function buildClassesPageQuery(state: ClassesPageUrlState): string {
  const params = new URLSearchParams();

  if (state.search.trim()) {
    params.set("q", state.search.trim());
  }
  if (state.season) {
    params.set("season", state.season);
  }
  if (state.location) {
    params.set("location", state.location);
  }
  if (state.teacher) {
    params.set("teacher", state.teacher);
  }
  if (state.days.length > 0) {
    params.set("days", state.days.join(","));
  }
  if (state.page > 1) {
    params.set("page", String(state.page));
  }

  const query = params.toString();
  return query ? `?${query}` : "";
}

export function classesPageParamsMatchSearchParams(
  state: ClassesPageUrlState,
  searchParams: URLSearchParams,
): boolean {
  return classesPageUrlStateEqual(
    state,
    parseClassesPageParamsFromSearchParams(searchParams),
  );
}
